import React from "react";
import {Controller, useFieldArray, useFormContext} from "react-hook-form";
import {Box, Chip, Colors, IconApproved, IconButton, IconCalendar, IconTrash, Text} from "@bounce/shared/ui";
import {IconAlertCircle} from "../../../../assets";
import {Button, TextField} from "../../../../ui/components";
import ChipsSelectInput from "./ChipsSelectInput";
import {HOURS} from "./constants";
import {formatDays, getScheduleCoverage} from "./helpers";
import {PolicyBlock} from "./PolicyBlock";
import {FormValues} from "./schema";
import {usePermittedHours} from "./usePermittedHours";

const DAYS_COLUMN_WIDTH = 120;
const REMOVE_BUTTON_SIZE = 36;
const COVERAGE_ICON_SIZE = 14;
const COVERAGE_ICON_GAP = "2px";

const COVERAGE_ICON_COLOR = {
  success: Colors.green._900,
  warning: Colors.orange._900,
  error: Colors.red._900,
} as const;

const CoverageChip = ({variantIndex}: {variantIndex: number}) => {
  const {watch} = useFormContext<FormValues>();
  const schedule = watch(`variants.${variantIndex}.policy.schedule`);
  const maxDay = watch(`variants.${variantIndex}.policy.max_day`);
  if (!Number.isFinite(maxDay) || maxDay < 1) return null;

  const {uncovered, overlapping} = getScheduleCoverage(schedule, maxDay);
  const issues = [
    uncovered.length > 0 && `Days ${formatDays(uncovered)} not covered`,
    overlapping.length > 0 && `Days ${formatDays(overlapping)} overlap`,
  ].filter(Boolean);
  const label = issues.length > 0 ? issues.join(" · ") : `Days 1–${maxDay} covered, no overlaps`;

  const variant = overlapping.length > 0 ? "error" : uncovered.length > 0 ? "warning" : "success";

  const Icon = variant === "success" ? IconApproved : IconAlertCircle;

  return (
    <Chip
      size_="small"
      variant_={variant}
      label={label}
      sx={{"& .MuiChip-icon": {marginLeft: 0, marginRight: COVERAGE_ICON_GAP}}}
      icon={
        <Icon
          width={COVERAGE_ICON_SIZE}
          height={COVERAGE_ICON_SIZE}
          style={{color: COVERAGE_ICON_COLOR[variant], flexShrink: 0}}
        />
      }
    />
  );
};

const ScheduleBlock = ({variantIndex}: {variantIndex: number}) => {
  const {control} = useFormContext<FormValues>();
  const schedulePath = `variants.${variantIndex}.policy.schedule` as const;
  const {fields, append, remove} = useFieldArray({control, name: schedulePath});
  const {isNotPermitted} = usePermittedHours(variantIndex);

  return (
    <PolicyBlock title="Schedule" icon={IconCalendar} action={<CoverageChip variantIndex={variantIndex} />}>
      <Box sx={{display: "flex", flexDirection: "column", gap: 1}}>
        <Box sx={{display: "flex", gap: 1}}>
          <Text size={12} variant="inter600" sx={{width: DAYS_COLUMN_WIDTH, color: Colors.grey._700}}>
            Day(s)
          </Text>
          <Text size={12} variant="inter600" sx={{flex: 1, color: Colors.grey._700}}>
            Hours
          </Text>
        </Box>
        {fields.map((field, index) => (
          <Box key={field.id} sx={{display: "flex", gap: 1, alignItems: "flex-start"}}>
            <Controller
              name={`${schedulePath}.${index}.days`}
              control={control}
              render={({field: {ref, ...daysField}, fieldState}) => (
                <TextField
                  {...daysField}
                  inputRef={ref}
                  size="small"
                  placeholder="4-14"
                  error={!!fieldState.error}
                  slotProps={{htmlInput: {"aria-label": `Schedule row ${index + 1} days`}}}
                  sx={{width: DAYS_COLUMN_WIDTH, backgroundColor: Colors.bw.white, borderRadius: "8px"}}
                />
              )}
            />
            <Box sx={{flex: 1}}>
              <Controller
                name={`${schedulePath}.${index}.hours`}
                control={control}
                render={({field: hoursField}) => (
                  <ChipsSelectInput
                    value={hoursField.value}
                    options={HOURS}
                    isInvalid={isNotPermitted}
                    onChange={hoursField.onChange}
                    placeholder="+ hour"
                    ariaLabel={`Schedule row ${index + 1} hours`}
                  />
                )}
              />
            </Box>
            <IconButton
              size="small"
              onClick={() => remove(index)}
              aria-label={`Remove schedule row ${index + 1}`}
              sx={{
                border: `1px solid ${Colors.tertiary._300}`,
                borderRadius: "8px",
                width: REMOVE_BUTTON_SIZE,
                height: REMOVE_BUTTON_SIZE,
                color: Colors.grey._700,
                "& svg path": {stroke: "currentColor"},
              }}
            >
              <Box component={IconTrash} sx={{width: 16, height: 16}} />
            </IconButton>
          </Box>
        ))}
      </Box>
      <Button
        buttonType="outlined-primary"
        size="small"
        onClick={() => append({days: "", hours: []})}
        sx={{alignSelf: "flex-start"}}
      >
        + Add day or range
      </Button>
    </PolicyBlock>
  );
};

export default ScheduleBlock;
